import React from "react";
import { ArchMap, LINK_COLORS } from "../archmap/ArchMap";
import { MapCard, MapLink, MapNode } from "../archmap/types";
import { DNS_PROVIDERS, Domain, domains, IP_ADINO, IP_VM108, IP_VM63, MAIN_SITE, openItems } from "./domains";

const SCENARIO_TAG: Record<Domain["scenario"], string> = {
  A: "A · Plesk",
  B: "B · nginx",
  C: "C · WordPress",
  main: "main site",
  unknown: "?",
};

// Rule node of a domain on the server its A record points to
const ruleOf = (d: Domain) => (d.scenario === "A" ? `p-${d.id.slice(2)}` : `s-${d.id.slice(2)}`);
const targetOf = (d: Domain) => `t-${d.id.slice(2)}`;

const COLUMNS = [["c-domains"], ["c-dns", "c-admins"], ["c-plesk", "c-vm63", "c-vm108"], ["c-target"]];

const site = (d: Domain): MapNode => ({
  id: ruleOf(d),
  name: d.nginxFile ?? d.domain,
  kind: "site",
  mono: true,
  tag: d.scenario === "B" ? (d.id === "d-eggenberger" ? "301 map" : "301") : d.scenario === "C" || d.scenario === "main" ? "WordPress" : "?",
  state: d.scenario === "unknown" ? "warn" : undefined,
  group: d.forge ? "Forge sites" : "Manual sites (not in Forge)",
  note: d.note,
  details: [
    ["File", `/etc/nginx/sites-available/${d.nginxFile}`],
    ["SSL", d.ssl],
    ["Managed by", d.managedBy],
  ],
});

const cards: MapCard[] = [
  {
    id: "c-domains",
    title: "Domains",
    description: "What the visitor types in the browser. Hover a domain to follow it to its rule and target page",
    accent: LINK_COLORS.dns,
    nodes: domains.map((d) => ({
      id: d.id,
      name: d.domain,
      kind: "client",
      mono: true,
      tag: SCENARIO_TAG[d.scenario],
      state: d.scenario === "unknown" ? "warn" : undefined,
      note: d.note,
      url: `https://${d.domain}`,
      related: d.target ? [targetOf(d)] : [],
      details: [
        ["DNS", d.dns === "unknown" ? "to verify" : DNS_PROVIDERS[d.dns].name],
        ["A record", d.a ? `${d.a}${d.aNote ? ` (${d.aNote})` : ""}` : "to verify"],
        ["Target", d.target ? `${MAIN_SITE}${d.target}` : "–"],
      ],
    })),
  },
  {
    id: "c-dns",
    title: "DNS authority",
    subtitle: "nslookup -type=NS <domain>",
    description: "Who controls the records of the domain. Only there a change of the A record has an effect",
    accent: LINK_COLORS.dns,
    nodes: (["adino", "azure"] as const).map((key) => ({
      id: `dns-${key}`,
      name: DNS_PROVIDERS[key].name,
      tag: key === "adino" ? "most domains" : "asr.ch only",
      kind: "dns",
      note: key === "adino" ? "Also holds old asr.ch records – they have no effect" : "Also holds an empty zone laendleerde.at – it has no effect",
      details: [
        ["Name servers", DNS_PROVIDERS[key].ns.join(", ")],
        ["Edit in", DNS_PROVIDERS[key].panel],
      ],
    })),
  },
  {
    id: "c-admins",
    title: "Who changes what",
    accent: "#6a1b9a",
    nodes: [
      {
        id: "itadmin",
        name: "Internal IT",
        tag: "SSH · WinSCP · Plesk",
        kind: "security",
        note: "Edits sites-available on VM63 (sudo), .htaccess in Plesk and DNS records",
      },
      {
        id: "forge",
        name: "Laravel Forge (agency)",
        tag: "forge.laravel.com",
        kind: "security",
        note: "The agency's tool; writes nginx configs and SSL certificates on VM63 over SSH and may overwrite manual edits",
      },
    ],
  },
  {
    id: "c-plesk",
    title: "Adino web hosting (Plesk)",
    subtitle: `${IP_ADINO} · srv280.adino.at`,
    description: "Ports 80/443 · rules in the .htaccess file of each domain",
    badge: { label: "Scenario A", color: "#ef6c00" },
    accent: "#ef6c00",
    nodes: [
      ...domains.filter((d) => d.scenario === "A").map((d) => ({ ...site(d), name: `${d.domain} .htaccess`, tag: "301", group: "Active", details: [["Where", "Plesk → domain → File Manager → .htaccess"], ["SSL", d.ssl]] as [string, string][] })),
      ...["eggenberger", "laendleerde"].map((key) => ({
        id: `p-${key}`,
        name: `${domains.find((d) => d.id === `d-${key}`)!.domain} .htaccess`,
        kind: "site" as const,
        mono: true,
        state: "disabled" as const,
        group: "Leftovers (no effect)",
        note: "The domain points to VM63, so this .htaccess is never used",
      })),
    ],
  },
  {
    id: "c-vm63",
    title: "Azure VM 63 · nginx",
    subtitle: `${IP_VM63} · gsc-r1-pr-vml-loackerwebsite-1`,
    description: "Ports 80/443 · nginx picks the file whose server_name matches the domain",
    badge: { label: "Scenario B / C", color: "#2e7d32" },
    chips: ["nginx 1.22.1", "sites-available → sites-enabled"],
    accent: "#2e7d32",
    details: [
      ["Config", "/etc/nginx/sites-available/<domain>"],
      ["Active when", "symlink in /etc/nginx/sites-enabled/"],
      ["Backups", "/home/loackercc/backups_emmanuel_dns/"],
    ],
    nodes: [
      ...domains.filter((d) => d.nginxFile).sort((a, b) => Number(b.forge) - Number(a.forge)).map(site),
      {
        id: "s-catchall",
        name: "000-catch-all",
        tag: "server_name _",
        kind: "site",
        mono: true,
        state: "disabled",
        group: "No match",
        note: "Answers every domain without its own file: empty response or TLS rejected",
      },
    ],
  },
  {
    id: "c-vm108",
    title: "Old ASR VM 108",
    subtitle: IP_VM108,
    badge: { label: "Shutdown candidate", color: "#9e9e9e" },
    accent: "#9e9e9e",
    nodes: [{ id: "vm108-web", name: "gsc-r1-pr-vml-asrwebsite-1", kind: "external", state: "stopped", note: "No DNS record points here anymore" }],
  },
  {
    id: "c-target",
    title: "loacker-recycling.com",
    subtitle: MAIN_SITE,
    description: "Target pages of the 301 redirects, served by the main WordPress on VM63",
    accent: "#d84315",
    nodes: domains
      .filter((d) => d.target)
      .map((d) => ({
        id: targetOf(d),
        name: d.target!,
        kind: "app" as const,
        mono: true,
        url: `${MAIN_SITE}${d.target}`,
        note: d.targetNote,
        related: ["s-loacker"],
        details: [["From", d.domain]] as [string, string][],
      })),
  },
];

const links: MapLink[] = [
  ...domains.flatMap((d): MapLink[] => [
    ...(d.dns !== "unknown" ? [{ from: d.id, to: `dns-${d.dns}`, kind: "dns" as const, label: "NS lookup" }] : []),
    ...(d.a ? [{ from: d.id, to: ruleOf(d), kind: "proxy" as const, label: `A ${d.a}` }] : []),
    ...(!d.a && d.nginxFile ? [{ from: d.id, to: ruleOf(d), kind: "proxy" as const, label: "file exists, DNS to verify", assumed: true }] : []),
    ...(d.target ? [{ from: ruleOf(d), to: targetOf(d), kind: "redirect" as const, label: "301" }] : []),
  ]),
  { from: "d-asr", to: "vm108-web", kind: "proxy", label: "old A alias (removed)", assumed: true },
  { from: "s-laendleerde", to: "s-loacker", kind: "proxy", label: "same WordPress, domain mapping" },
  ...domains.filter((d) => d.forge && d.nginxFile).map((d): MapLink => ({ from: "forge", to: ruleOf(d), kind: "manage", label: "writes config + SSL" })),
  { from: "itadmin", to: "s-asr", kind: "manage", label: "manual file + certbot" },
  { from: "itadmin", to: "s-weiss", kind: "manage", label: "edits the redirect" },
  { from: "itadmin", to: "s-eggenberger", kind: "manage", label: "edits the redirects" },
  { from: "itadmin", to: "p-kuster", kind: "manage", label: "Plesk .htaccess + SSL" },
  { from: "itadmin", to: "dns-adino", kind: "manage", label: "A records in nsentry" },
  { from: "itadmin", to: "dns-azure", kind: "manage", label: "A records in Azure Portal" },
];

export const WebsitesMap: React.FunctionComponent = () => (
  <ArchMap
    title="Loacker Websites"
    subtitle={`Domain → DNS authority → A record → rule on that server → ${MAIN_SITE} · verified 2026-11-03`}
    stats={[
      { label: "Domains", value: domains.length },
      { label: "Redirects", value: domains.filter((d) => d.target).length },
      { label: "Web servers", value: 2 },
      { label: "To verify", value: domains.filter((d) => d.scenario === "unknown").length },
    ]}
    cards={cards}
    columns={COLUMNS}
    gridTemplate="minmax(230px, 1fr) minmax(200px, 0.9fr) minmax(250px, 1.1fr) minmax(230px, 1fr)"
    links={links}
    legend={[
      { label: "A · .htaccess on Adino", color: "#ef6c00" },
      { label: "B/C · nginx on VM63", color: "#2e7d32" },
      { label: "Retired", color: "#9e9e9e" },
    ]}
    issues={openItems}
  />
);
